import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PaginatedTable, PaginatedTableCallbacks, useTableUrlSync } from '../common'
import { RecordsTable } from './'
import { JsonRecord, JsonUser, Record, User } from '../../models'
import { getRecords, getUnassignedRecordCount, getUsers } from '../../api'
import { downloadRecordsCsv } from '../../api/routes'
import { getAuthUser } from '../../helpers'
import defineAbilityFor from './defineAbility'
import { InsufficientPermissions } from '../common'
import { RecordStatusFilters } from './filters'
import { AssignRecordsForm } from './assignRecordsForm'
import { AdvancedSearch } from './advancedSearch'

const defaultSearchState = {
    rec: '',
    firstName: '',
    lastName: '',
    conf_no: '',
    phone: '',
    agency: '',
    din: '',
    out: '',
    supplierPhone: '',
    supplierId: '',
    assignedUserId: '',
    entry: '',
}
const advancedSearchKeys = Object.keys(defaultSearchState)

export const RecordsLanding = () => {
    const ability = defineAbilityFor(getAuthUser())
    const [searchParams, setSearchParams] = useSearchParams()
    const tableUrlSync = useTableUrlSync(
        ['record_status_code', 'record_substatus_code'],
        advancedSearchKeys
    )
    const [users, setUsers] = useState<User[]>([])
    const [usersLoaded, setUsersLoaded] = useState(false)
    const sortBy = tableUrlSync.sortBy || searchParams.get('sortBy')
    const sortDirection = tableUrlSync.sortDirection ?? searchParams.get('sortDirection')
    const setAdvancedSearchRef = useRef<((values: { [key: string]: any }) => void) | null>(null)
    const updateFiltersRef = useRef<((filterName: string, newValue: any, shouldDelete?: boolean, dependentFilterToClear?: string) => void) | null>(null)
    const defaultFilters = useMemo(() => {
        const filters: { [key: string]: string } = {}
        const status = searchParams.get('record_status_code')
        const substatus = searchParams.get( 'record_substatus_code')

        if (status) filters.record_status_code = status
        if (substatus) filters.record_substatus_code = substatus

        return filters
    }, [searchParams])

    const defaultAdvancedSearchState = useMemo(
        () => {
        const state = { ...defaultSearchState }
        advancedSearchKeys.forEach((key) => {
            const value = searchParams.get(key)
            if (value !== null) {
                state[key as keyof typeof defaultSearchState] = value
            }})
        return state
    }, [searchParams])
    const searchInputs = useMemo(
        () => [
            { id: 'rec', name: 'rec', placeholder: 'Record ID', type: 'text' },
            { id: 'firstName', name: 'firstName', placeholder: 'First Name', type: 'text' },
            { id: 'lastName', name: 'lastName', placeholder: 'Last Name', type: 'text' },
            { id: 'phone', name: 'phone', placeholder: 'Phone', type: 'text' },
            { id: 'conf_no', name: 'conf_no', placeholder: 'Confirmation no.', type: 'text' },
            { id: 'agency', name: 'agency', placeholder: 'Agency', type: 'text' },
            {
                id: 'supplierPhone',
                name: 'supplierPhone',
                placeholder: 'Supplier Phone',
                type: 'text',
            },
            { id: 'supplierId', name: 'supplierId', placeholder: 'Supplier ID', type: 'text' },
            {
                id: 'din',
                name: 'din',
                placeholder: 'Check in',
                type: 'date',
                label: 'Check in',
            },
            {
                id: 'out',
                name: 'out',
                placeholder: 'Check out',
                type: 'date',
                label: 'Check out',
            },
            {
                id: 'assignedUserId',
                name: 'assignedUserId',
                label: 'Select Assigned User',
                type: 'dropdown',
                options: [...users.map((user) => ({ value: user.id, label: user.name }))],
            },
            {
                id: 'entry',
                name: 'entry',
                placeholder: 'Upload Date',
                type: 'date',
                label: 'Upload Date',
            },
        ],
        [users]
    )

    useEffect(() => {
        getUsers()
            .then((json) => {
                setUsers(json.data.map((user: JsonUser) => new User(user)))
                setUsersLoaded(true)
            })
            .catch(() => {})
    }, [])

    const handleClear = useCallback(() => {
        setAdvancedSearchRef.current?.({})
        updateFiltersRef.current?.('record_status_code', '', true, 'record_substatus_code')

        const params = new URLSearchParams(searchParams)
        advancedSearchKeys.forEach((key) => params.delete(key))
        params.delete('record_status_code')
        params.delete('record_substatus_code')

        if (params.toString() !== searchParams.toString()) {
            setSearchParams(params, { replace: true })
        }
    }, [searchParams, setSearchParams])

    if (!ability.can('View', 'Record')) {
        return <InsufficientPermissions />
    }

    return (
        <PaginatedTable<Record, JsonRecord>
            DownloadButton={({ advancedSearch, params }) => {
                const searchParams = new URLSearchParams(params)

                searchParams.set('advancedSearch', JSON.stringify(advancedSearch))

                return (
                    <a
                        href={downloadRecordsCsv + '?' + searchParams.toString()}
                        className="btn btn-primary"
                    >
                        Download CSV
                    </a>
                )
            }}
            getData={(params) =>
                getRecords({
                    ...params,
                    ...(sortBy ? { sortBy } : {}),
                    ...(sortDirection ? { sortDirection } : {}),
                })
            }
            Filters={(props) => {
                updateFiltersRef.current = props.updateFilters
                return (<RecordStatusFilters
                        {...props}
                        defaultFilters={defaultFilters}
                    />
                )}}
            Form={
                ability.can('AssignToUser', 'Record') && usersLoaded
                    ? (props) => (
                          <AssignRecordsForm
                              users={users}
                              {...props}
                              getRecordCount={getUnassignedRecordCount}
                          />
                      )
                    : undefined
            }
            redirectLink={{
                to: '/records/add',
                text: 'Create New Record',
            }}
            AdvancedSearchComponent={AdvancedSearch}
            advancedSearchProps={{
                users,
                inputs: searchInputs,
                defaultState: defaultSearchState,
                onClear: handleClear,
            }}
            setAdvancedSearchRef={setAdvancedSearchRef}
            defaultAdvancedSearchState={defaultAdvancedSearchState}
            defaultFilters={defaultFilters}
            defaultPage={searchParams.get('page') ?? '1'}
            onParamsChange={tableUrlSync.onParamsChange}
            modelConstructor={(json: JsonRecord) => new Record(json)}
            disableSearch={true}
            Table={(records: Record[], callbacks: PaginatedTableCallbacks) => (
                <RecordsTable callbacks={callbacks} resources={{ records }} />
            )}
            title="Records"
        />
    )
}
